import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { TableSkeleton } from '../components/Skeleton';
import {
  Users, Plus, Search, Upload, Mail, Phone, Edit, Trash2,
  X, Save, Download, RefreshCw, UserPlus, MessageCircle, Tag
} from 'lucide-react';
import PhoneCountryField from '../components/PhoneCountryField';
import {
  DEFAULT_COUNTRY_CODE,
  combinePhoneDigits,
  parseStoredPhoneForInputs,
  COUNTRY_OPTIONS,
  phoneFieldHintLine
} from '../utils/phoneCountry';

const Contacts = () => {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Debug: Log every render

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // Form states
  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    email: '',
    subscription_status: 'subscribed',
    group_name: '',
    tags: []
  });

  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  /** Default country for CSV: 10-digit rows get this prefix (India 91) */
  const [importCountryCode, setImportCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [updateOnDuplicate, setUpdateOnDuplicate] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [newPhoneCountry, setNewPhoneCountry] = useState(DEFAULT_COUNTRY_CODE);
  const [newPhoneNational, setNewPhoneNational] = useState('');
  const [editPhoneCountry, setEditPhoneCountry] = useState(DEFAULT_COUNTRY_CODE);
  const [editPhoneNational, setEditPhoneNational] = useState('');
  const [bulkEditLoading, setBulkEditLoading] = useState(false);

  // Search states for dropdowns
  const [groupSearch, setGroupSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showEditGroupDropdown, setShowEditGroupDropdown] = useState(false);
  const [showEditTagDropdown, setShowEditTagDropdown] = useState(false);
  const [showBulkGroupDropdown, setShowBulkGroupDropdown] = useState(false);
  const [showBulkTagDropdown, setShowBulkTagDropdown] = useState(false);

  // Bulk operations states
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // Refs for dropdown containers
  const groupDropdownRef = useRef(null);
  const tagDropdownRef = useRef(null);
  const editGroupDropdownRef = useRef(null);
  const editTagDropdownRef = useRef(null);
  const bulkGroupDropdownRef = useRef(null);
  const bulkTagDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
      if (editGroupDropdownRef.current && !editGroupDropdownRef.current.contains(event.target)) {
        setShowEditGroupDropdown(false);
      }
      if (editTagDropdownRef.current && !editTagDropdownRef.current.contains(event.target)) {
        setShowEditTagDropdown(false);
      }
      if (bulkGroupDropdownRef.current && !bulkGroupDropdownRef.current.contains(event.target)) {
        setShowBulkGroupDropdown(false);
      }
      if (bulkTagDropdownRef.current && !bulkTagDropdownRef.current.contains(event.target)) {
        setShowBulkTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset search states when modals close
  useEffect(() => {
    if (!showCreateModal) {
      setGroupSearch('');
      setTagSearch('');
      setShowGroupDropdown(false);
      setShowTagDropdown(false);
    }
  }, [showCreateModal]);

  useEffect(() => {
    if (!showEditModal) {
      setGroupSearch('');
      setTagSearch('');
      setShowEditGroupDropdown(false);
      setShowEditTagDropdown(false);
    }
  }, [showEditModal]);

  useEffect(() => {
    if (!showBulkEditModal) {
      setGroupSearch('');
      setTagSearch('');
      setShowBulkGroupDropdown(false);
      setShowBulkTagDropdown(false);
    }
  }, [showBulkEditModal]);
  const [bulkEditData, setBulkEditData] = useState({
    action: 'set_group', // 'set_group', 'remove_group', 'add_tags', 'remove_tags', 'replace_tags', 'set_subscription_status'
    group: '',
    tags: [],
    subscription_status: 'subscribed'
  });

  // Sync from messages loading state
  const [syncingFromMessages, setSyncingFromMessages] = useState(false);

  // Fetch contacts using React Query
  const { data: contactsData, isLoading: loading } = useQuery(
    ['contacts', currentWorkspace?.id, searchTerm, selectedGroup, selectedTag, selectedStatus, startDate, endDate, currentPage, pageSize],
    async () => {
      if (!currentWorkspace?.id) {
        return { contacts: [], pagination: { total: 0, pages: 0 } };
      }

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedGroup) params.append('group', selectedGroup);
      if (selectedTag) params.append('tag', selectedTag);
      if (selectedStatus) params.append('subscription_status', selectedStatus);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get(`/api/contacts?${params.toString()}`, { headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Error loading contacts:', error);
        toast.error('Failed to load contacts');
      }
    }
  );

  // Fetch groups and tags using React Query
  const { data: groupsData } = useQuery(
    ['contactGroups', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) {
        return { groups: [] };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/contacts/groups/list', { headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Error loading groups:', error);
      }
    }
  );

  // Fetch tags using React Query
  const { data: tagsData } = useQuery(
    ['contactTags', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) {
        return { tags: [] };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/contacts/tags/list', { headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Error loading tags:', error);
      }
    }
  );



  const contacts = contactsData?.contacts || [];
  const groups = groupsData?.groups || [];
  const tags = tagsData?.tags || [];
  const pagination = contactsData?.pagination || { total: 0, pages: 0, page: 1, limit: 20 };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGroup, selectedTag]);

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewPhoneCountry(DEFAULT_COUNTRY_CODE);
    setNewPhoneNational('');
    setNewContact({
      name: '',
      phone: '',
      email: '',
      subscription_status: 'subscribed',
      group_name: '',
      tags: []
    });
  };

  // Create contact
  const handleCreateContact = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      // Clean up the data - convert empty strings to null for optional fields
      const phoneCombined = combinePhoneDigits(newPhoneCountry, newPhoneNational).trim();
      const cleanedContact = {
        ...newContact,
        phone: phoneCombined || null,
        email: newContact.email?.trim() || null,
        group_name: newContact.group_name?.trim() || null,
        tags: newContact.tags || []
      };

      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.post('/api/contacts', cleanedContact, { headers });
      queryClient.invalidateQueries(['contacts', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactGroups', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactTags', currentWorkspace?.id]);
      closeCreateModal();
      toast.success('Contact created successfully');
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error(error.response?.data?.error || 'Failed to create contact');
    } finally {
      setCreateLoading(false);
    }
  };

  // Update contact
  const handleUpdateContact = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    try {
      // Clean up the data - convert empty strings to null for optional fields
      const phoneCombined = combinePhoneDigits(editPhoneCountry, editPhoneNational).trim();
      const cleanedContact = {
        ...editingContact,
        phone: phoneCombined || null,
        email: editingContact.email?.trim() || null,
        subscription_status: editingContact.subscription_status || 'subscribed',
        group_name: editingContact.group_name?.trim() || null,
        tags: editingContact.tags || []
      };

      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.put(`/api/contacts/${editingContact.id}`, cleanedContact, { headers });
      queryClient.invalidateQueries(['contacts', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactGroups', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactTags', currentWorkspace?.id]);
      setShowEditModal(false);
      setEditingContact(null);
      setEditPhoneCountry(DEFAULT_COUNTRY_CODE);
      setEditPhoneNational('');
      toast.success('Contact updated successfully');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.error || 'Failed to update contact');
    } finally {
      setUpdateLoading(false);
    }
  };

  // Delete contact
  const handleDeleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.delete(`/api/contacts/${id}`, { headers });
      queryClient.invalidateQueries(['contacts', currentWorkspace?.id]);
      toast.success('Contact deleted successfully');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  // Edit contact
  const handleEditContact = (contact) => {
    const parsed = parseStoredPhoneForInputs(contact.phone, DEFAULT_COUNTRY_CODE);
    setEditPhoneCountry(parsed.countryCode);
    setEditPhoneNational(parsed.national);
    setEditingContact({
      ...contact,
      phone: contact.phone || '',
      email: contact.email || '',
      subscription_status: contact.subscription_status || 'subscribed',
      group_name: contact.group_name || '',
    });
    setShowEditModal(true);
  };

  // Start chat with contact
  const handleStartChat = async (contact) => {
    if (!contact.phone) {
      toast.error('This contact has no phone number');
      return;
    }

    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      // Create or get chat session
      const response = await apiClient.post('/api/chats/sessions', {
        contact_number: contact.phone,
        contact_name: contact.name
      }, { headers });

      if (response.data.success) {
        toast.success(`Chat opened with ${contact.name}`);
        // Navigate to chats page with the contact phone to auto-select
        navigate('/chats', { state: { openContact: contact.phone, contactName: contact.name } });
      }
    } catch (error) {
      // If session already exists, that's okay - just navigate to chats
      if (error.response?.status === 400 && error.response?.data?.error?.includes('already exists')) {
        toast.success(`Opening chat with ${contact.name}`);
        // Navigate to chats page with the contact phone to auto-select
        navigate('/chats', { state: { openContact: contact.phone, contactName: contact.name } });
      } else {
        console.error('Error creating chat session:', error);
        toast.error(error.response?.data?.error || 'Failed to open chat');
      }
    }
  };

  // Import CSV
  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImportLoading(true);
    setImportProgress(0);

    // Simulate progress (since we can't get real-time progress from backend)
    const progressInterval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 90) return prev; // Don't go to 100% until request completes
        return prev + Math.random() * 10;
      });
    }, 200);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('updateOnDuplicate', updateOnDuplicate);
      formData.append('defaultCountryCode', importCountryCode);

      const headers = currentWorkspace?.id ? {
        'Content-Type': 'multipart/form-data',
        'x-workspace-id': currentWorkspace.id
      } : { 'Content-Type': 'multipart/form-data' };

      const response = await apiClient.post('/api/contacts/import', formData, { headers });

      clearInterval(progressInterval);
      setImportProgress(100);

      // Show detailed success message
      let successMsg = response.data.message;
      if (response.data.updated > 0 || response.data.skipped > 0) {
        successMsg = `Processed ${response.data.imported || 0} inserted`;
        if (response.data.updated > 0) successMsg += `, ${response.data.updated} updated`;
        if (response.data.skipped > 0) successMsg += `, ${response.data.skipped} skipped`;
        if (response.data.totalErrors > 0) successMsg += `, ${response.data.totalErrors} errors`;
      }

      if (response.data.errors && response.data.errors.length > 0) {
        toast.success(successMsg, { duration: 5000 });
        console.log('Import errors:', response.data.errors);
      } else {
        toast.success(successMsg, { duration: 5000 });
      }

      // Wait a moment to show 100% progress, then close
      setTimeout(() => {
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview([]);
        setUpdateOnDuplicate(false);
        setImportCountryCode(DEFAULT_COUNTRY_CODE);
        setImportLoading(false);
        setImportProgress(0);
        queryClient.invalidateQueries(['contacts', currentWorkspace?.id]);
        queryClient.invalidateQueries(['contactGroups', currentWorkspace?.id]);
      }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      setImportProgress(0);
      setImportLoading(false);
      console.error('Error importing contacts:', error);
      const errorMsg = error.response?.data?.error || 'Failed to import contacts';
      const details = error.response?.data?.details;

      if (details && Array.isArray(details)) {
        toast.error(`${errorMsg}. Check console for details.`);
        console.log('Import error details:', details);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  // Preview CSV
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFile(file);

    // Preview first 5 rows
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').slice(0, 6); // First 5 data rows + header
      const preview = lines.map(line => line.split(','));
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };


  // Export contacts
  const handleExportContacts = () => {
    const csvContent = [
      ['Name', 'Phone', 'Email', 'Group', 'Tags'].join(','),
      ...contacts.map(contact => [
        contact.name,
        contact.phone || '',
        contact.email || '',
        contact.group_name || '',
        contact.tags ? contact.tags.join('; ') : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Contacts exported successfully');
  };

  // Sync contacts from messages
  const handleSyncFromMessages = async () => {
    if (!window.confirm('This will add new contacts from your message history. Continue?')) return;

    setSyncingFromMessages(true);
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.post('/api/contacts/sync-from-messages', {}, { headers });
      toast.success(response.data.message);
      queryClient.invalidateQueries(['contacts', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactGroups', currentWorkspace?.id]);
    } catch (error) {
      console.error('Error syncing contacts from messages:', error);
      toast.error(error.response?.data?.error || 'Failed to sync contacts from messages');
    } finally {
      setSyncingFromMessages(false);
    }
  };

  // Bulk operations
  const handleSelectContact = (contactId, isSelected) => {
    if (isSelected) {
      setSelectedContacts([...selectedContacts, contactId]);
    } else {
      setSelectedContacts(selectedContacts.filter(id => id !== contactId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedContacts(contacts.map(contact => contact.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Please select contacts to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedContacts.length} contacts?`)) return;

    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      // Bulk delete request processed

      const response = await apiClient.delete('/api/contacts/bulk', {
        headers,
        data: { contactIds: selectedContacts }
      });

      toast.success(`Successfully deleted ${response.data.deletedCount} contacts`);
      setSelectedContacts([]);
      queryClient.invalidateQueries(['contacts', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactGroups', currentWorkspace?.id]);
    } catch (error) {
      console.error('Error deleting contacts:', error);
      console.error('Error details:', error.response?.data);

      let errorMessage = 'Failed to delete contacts';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (error.response.data.details) {
          console.log('Error details:', error.response.data.details);
        }
      }

      toast.error(errorMessage);
    }
  };

  const handleBulkEdit = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Please select contacts to edit');
      return;
    }

    // Validate group selection for set_group action
    if (bulkEditData.action === 'set_group' && !bulkEditData.group) {
      toast.error('Please select a group or create a new one');
      return;
    }

    // Validate tags selection for tag actions
    if ((bulkEditData.action === 'add_tags' || bulkEditData.action === 'remove_tags' || bulkEditData.action === 'replace_tags') && (!bulkEditData.tags || bulkEditData.tags.length === 0)) {
      toast.error('Please select at least one tag');
      return;
    }

    // Validate subscription status for subscription action
    if (bulkEditData.action === 'set_subscription_status' && !bulkEditData.subscription_status) {
      toast.error('Please select a subscription status');
      return;
    }

    setBulkEditLoading(true);
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const requestData = {
        contactIds: selectedContacts,
        action: bulkEditData.action,
        group: bulkEditData.group || null,
        tags: bulkEditData.tags || [],
        subscription_status: bulkEditData.subscription_status || 'subscribed'
      };

      console.log('Bulk edit request:', requestData);

      const response = await apiClient.put('/api/contacts/bulk', requestData, { headers });

      toast.success(`Successfully updated ${response.data.updatedCount} contacts`);
      setSelectedContacts([]);
      setShowBulkEditModal(false);
      setBulkEditData({ action: 'set_group', group: '', tags: [], subscription_status: 'subscribed' });
      queryClient.invalidateQueries(['contacts', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactGroups', currentWorkspace?.id]);
      queryClient.invalidateQueries(['contactTags', currentWorkspace?.id]);
    } catch (error) {
      console.error('Error updating contacts:', error);
      toast.error(error.response?.data?.error || 'Failed to update contacts');
    } finally {
      setBulkEditLoading(false);
    }
  };


  return (
    <div className="bg-gray-50 -m-4 sm:-m-6 lg:-m-8 p-4 sm:p-6 lg:p-8 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contacts Management</h1>
            <p className="text-gray-600 mt-2">Organize and manage your contact database</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportContacts}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
            <button
              onClick={handleSyncFromMessages}
              disabled={syncingFromMessages}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${syncingFromMessages ? 'animate-spin' : ''}`} />
              <span>Sync Messages</span>
            </button>
            <button
              onClick={() => {
                setImportCountryCode(DEFAULT_COUNTRY_CODE);
                setShowImportModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Upload className="h-4 w-4" />
              <span>Import CSV</span>
            </button>
            <button
              onClick={() => {
                setNewPhoneCountry(DEFAULT_COUNTRY_CODE);
                setNewPhoneNational('');
                setShowCreateModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add Contact</span>
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedContacts.length > 0 && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2">
              <span className="bg-blue-600 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
                {selectedContacts.length}
              </span>
              Selected
            </div>
            <button
              onClick={() => setShowBulkEditModal(true)}
              className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center gap-2 transition-colors"
            >
              <Edit className="h-4 w-4" />
              <span>Bulk Edit</span>
            </button>
            <button
              onClick={handleBulkDelete}
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center gap-2 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        )}
      </div>

      {/* Unified Contacts Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6 pt-0">
        {/* Filters Section */}
        <div className="flex items-center justify-between mb-6">

          {(searchTerm || selectedGroup || selectedTag || selectedStatus) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedGroup('');
                setSelectedTag('');
                setSelectedStatus('');
              }}
              className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 flex mt-4  items-center gap-2 shadow-md hover:shadow-lg transition-all"
            >
              <div className="bg-white/20 backdrop-blur-sm rounded-full h-6 w-6 flex items-center justify-center ">
                <X className="h-4 w-4" />
              </div>
              <span>Clear All Filters</span>
              <span className="bg-white/30 backdrop-blur-sm px-2 py-0.5 rounded-full text-xs font-bold">
                {[searchTerm, selectedGroup, selectedTag, selectedStatus].filter(Boolean).length}
              </span>
            </button>
          )}
        </div>
        <div className="flex gap-3 mb-6 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="relative w-52">
            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-all"
            >
              <option value="">All Groups ({pagination.total})</option>
              {groups.map(group => (
                <option key={group.name || group} value={group.name === 'No Group' ? '' : (group.name || group)}>
                  {group.name || group} {group.count ? `(${group.count})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="relative w-48">
            <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-all"
            >
              <option value="">All Tags</option>
              {tags.map(tag => (
                <option key={tag.name || tag} value={tag.name === 'No Tags' ? '' : (tag.name || tag)}>
                  {tag.name || tag} {tag.count ? `(${tag.count})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="relative w-36">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-all"
            >
              <option value="">All Status</option>
              <option value="subscribed">Subscribed</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </div>
          <div className="relative w-32">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all text-sm"
              title="Start Date"
            />
          </div>
          <div className="relative w-32">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all text-sm"
              title="End Date"
            />
          </div>
          {(startDate || endDate) && (
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              className="px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center h-[42px] w-[42px]"
              title="Clear date filter"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Contacts Table */}
        <div className="overflow-hidden">
          {loading ? (
            <TableSkeleton rows={10} columns={5} />
          ) : contacts.length === 0 ? (
            <div className="p-16 text-center">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full h-24 w-24 flex items-center justify-center mx-auto mb-4">
                <Users className="h-12 w-12 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No contacts found</h3>
              <p className="text-gray-600 mb-6">Get started by adding your first contact or importing from CSV.</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => {
                setNewPhoneCountry(DEFAULT_COUNTRY_CODE);
                setNewPhoneNational('');
                setShowCreateModal(true);
              }}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
                >
                  <UserPlus className="h-5 w-5" />
                  <span>Add Contact</span>
                </button>
                <button
                  onClick={() => {
                setImportCountryCode(DEFAULT_COUNTRY_CODE);
                setShowImportModal(true);
              }}
                  className="bg-white text-blue-600 border-2 border-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 flex items-center gap-2 transition-all"
                >
                  <Upload className="h-5 w-5" />
                  <span>Import CSV</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedContacts.length === contacts.length && contacts.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Group
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {contacts.map((contact, index) => (
                    <tr
                      key={contact.id}
                      className="hover:bg-blue-50/50 transition-all duration-150 group"
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={(e) => handleSelectContact(contact.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                              <span className="text-white font-semibold text-sm">
                                {contact.name?.charAt(0).toUpperCase() || 'U'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-gray-900">{contact.name || 'Unnamed'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center text-sm text-gray-900">
                          <Mail className="h-4 w-4 text-gray-400 mr-2" />
                          <span>{contact.email || 'No email'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center text-sm text-gray-900">
                          <Phone className="h-4 w-4 text-gray-400 mr-2" />
                          <span>{contact.phone || 'No phone'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {contact.subscription_status === 'subscribed' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Subscribed
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            Unsubscribed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {contact.group_name ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200">
                            {contact.group_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">No group</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {contact.tags && contact.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-xs relative group">
                            {contact.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200 truncate max-w-[100px]"
                                title={tag}
                              >
                                {tag}
                              </span>
                            ))}
                            {contact.tags.length > 3 && (
                              <div className="relative">
                                <span
                                  className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-300 cursor-help hover:bg-gray-200 transition-colors"
                                  title={`All tags: ${contact.tags.join(', ')}`}
                                >
                                  +{contact.tags.length - 3} more
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No tags</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {contact.created_at ? (
                          new Date(contact.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleStartChat(contact)}
                            className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Send Message"
                            disabled={!contact.phone}
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditContact(contact)}
                            className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700 transition-all"
                            title="Edit Contact"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteContact(contact.id)}
                            className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 transition-all"
                            title="Delete Contact"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {!loading && contacts.length > 0 && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-500">per page</span>
              </div>
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} contacts
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.pages - 2) {
                    pageNum = pagination.pages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded-md ${pagination.page === pageNum
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(pagination.pages)}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Contact Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                    <UserPlus className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Add New Contact</h2>
                </div>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreateContact}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter contact name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <PhoneCountryField
                      idPrefix="create"
                      countryCode={newPhoneCountry}
                      nationalNumber={newPhoneNational}
                      onCountryChange={setNewPhoneCountry}
                      onNationalChange={setNewPhoneNational}
                      disabled={createLoading}
                    />
                    <p className="mt-1 text-xs text-gray-500">{phoneFieldHintLine(newPhoneCountry)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                    <select
                      value={newContact.subscription_status}
                      onChange={(e) => setNewContact({ ...newContact, subscription_status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="subscribed">Subscribed</option>
                      <option value="unsubscribed">Unsubscribed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                    <div className="relative" ref={groupDropdownRef}>
                      <input
                        type="text"
                        value={newContact.group_name || ''}
                        onChange={(e) => {
                          setNewContact({ ...newContact, group_name: e.target.value });
                          setGroupSearch(e.target.value);
                          setShowGroupDropdown(true);
                        }}
                        onFocus={() => setShowGroupDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Search or create group..."
                      />
                      {showGroupDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {groups
                            .filter(g => (g.name || g).toLowerCase().includes(groupSearch.toLowerCase()))
                            .map((group) => (
                              <div
                                key={group.name || group}
                                onClick={() => {
                                  setNewContact({ ...newContact, group_name: group.name || group });
                                  setGroupSearch('');
                                  setShowGroupDropdown(false);
                                }}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                              >
                                {group.name || group} {group.count ? `(${group.count})` : ''}
                              </div>
                            ))}
                          {groupSearch && !groups.find(g => (g.name || g).toLowerCase() === groupSearch.toLowerCase()) && (
                            <div
                              onClick={() => {
                                setNewContact({ ...newContact, group_name: groupSearch });
                                setGroupSearch('');
                                setShowGroupDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-600 border-t border-gray-200"
                            >
                              + Create "{groupSearch}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {newContact.group_name && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">{newContact.group_name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setNewContact({ ...newContact, group_name: '' });
                            setGroupSearch('');
                          }}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <div className="relative" ref={tagDropdownRef}>
                    <input
                      type="text"
                        value={tagSearch}
                        onChange={(e) => {
                          setTagSearch(e.target.value);
                          setShowTagDropdown(true);
                        }}
                        onFocus={() => setShowTagDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Search or add tags..."
                      />
                      {showTagDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {tags
                            .filter(t => (t.name || t).toLowerCase().includes(tagSearch.toLowerCase()))
                            .map((tag) => {
                              const isSelected = newContact.tags && newContact.tags.includes(tag.name || tag);
                              return (
                                <div
                                  key={tag.name || tag}
                                  onClick={() => {
                                    const currentTags = newContact.tags || [];
                                    if (isSelected) {
                                      setNewContact({ ...newContact, tags: currentTags.filter(t => t !== (tag.name || tag)) });
                                    } else {
                                      setNewContact({ ...newContact, tags: [...currentTags, tag.name || tag] });
                                    }
                                    setTagSearch('');
                                  }}
                                  className={`px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm flex items-center justify-between ${isSelected ? 'bg-blue-50' : ''
                                    }`}
                                >
                                  <span>{tag.name || tag} {tag.count ? `(${tag.count})` : ''}</span>
                                  {isSelected && <span className="text-blue-600">✓</span>}
                                </div>
                              );
                            })}
                          {tagSearch && !tags.find(t => (t.name || t).toLowerCase() === tagSearch.toLowerCase()) && (
                            <div
                              onClick={() => {
                                const currentTags = newContact.tags || [];
                                setNewContact({ ...newContact, tags: [...currentTags, tagSearch] });
                                setTagSearch('');
                                setShowTagDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-600 border-t border-gray-200"
                            >
                              + Add "{tagSearch}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {newContact.tags && newContact.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {newContact.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => {
                                setNewContact({ ...newContact, tags: newContact.tags.filter((_, i) => i !== index) });
                              }}
                              className="text-blue-700 hover:text-blue-900"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    disabled={createLoading}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed"
                  >
                    {createLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                    <Save className="h-4 w-4" />
                    <span>Save Contact</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {showEditModal && editingContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                    <Edit className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Edit Contact</h2>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={handleUpdateContact}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={editingContact.name}
                      onChange={(e) => setEditingContact({ ...editingContact, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <PhoneCountryField
                      idPrefix="edit"
                      countryCode={editPhoneCountry}
                      nationalNumber={editPhoneNational}
                      onCountryChange={setEditPhoneCountry}
                      onNationalChange={setEditPhoneNational}
                      disabled={updateLoading}
                    />
                    <p className="mt-1 text-xs text-gray-500">{phoneFieldHintLine(editPhoneCountry)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={editingContact.email || ''}
                      onChange={(e) => setEditingContact({ ...editingContact, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                    <select
                      value={editingContact.subscription_status || 'subscribed'}
                      onChange={(e) => setEditingContact({ ...editingContact, subscription_status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="subscribed">Subscribed</option>
                      <option value="unsubscribed">Unsubscribed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                    <div className="relative" ref={editGroupDropdownRef}>
                      <input
                        type="text"
                        value={editingContact.group_name || ''}
                        onChange={(e) => {
                          setEditingContact({ ...editingContact, group_name: e.target.value });
                          setGroupSearch(e.target.value);
                          setShowEditGroupDropdown(true);
                        }}
                        onFocus={() => setShowEditGroupDropdown(true)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Search or create group..."
                      />
                      {showEditGroupDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {groups
                            .filter(g => (g.name || g).toLowerCase().includes(groupSearch.toLowerCase()))
                            .map((group) => (
                              <div
                                key={group.name || group}
                                onClick={() => {
                                  setEditingContact({ ...editingContact, group_name: group.name || group });
                                  setGroupSearch('');
                                  setShowEditGroupDropdown(false);
                        }}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                              >
                                {group.name || group} {group.count ? `(${group.count})` : ''}
                              </div>
                            ))}
                          {groupSearch && !groups.find(g => (g.name || g).toLowerCase() === groupSearch.toLowerCase()) && (
                            <div
                              onClick={() => {
                                setEditingContact({ ...editingContact, group_name: groupSearch });
                                setGroupSearch('');
                                setShowEditGroupDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-600 border-t border-gray-200"
                            >
                              + Create "{groupSearch}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <div className="relative" ref={editTagDropdownRef}>
                    <input
                      type="text"
                        value={tagSearch}
                        onChange={(e) => {
                          setTagSearch(e.target.value);
                          setShowEditTagDropdown(true);
                        }}
                        onFocus={() => setShowEditTagDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Search or add tags..."
                      />
                      {showEditTagDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {tags
                            .filter(t => (t.name || t).toLowerCase().includes(tagSearch.toLowerCase()))
                            .map((tag) => {
                              const isSelected = editingContact.tags && editingContact.tags.includes(tag.name || tag);
                              return (
                                <div
                                  key={tag.name || tag}
                                  onClick={() => {
                                    const currentTags = editingContact.tags || [];
                                    if (isSelected) {
                                      setEditingContact({ ...editingContact, tags: currentTags.filter(t => t !== (tag.name || tag)) });
                                    } else {
                                      setEditingContact({ ...editingContact, tags: [...currentTags, tag.name || tag] });
                                    }
                                    setTagSearch('');
                                  }}
                                  className={`px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm flex items-center justify-between ${isSelected ? 'bg-blue-50' : ''
                                    }`}
                                >
                                  <span>{tag.name || tag} {tag.count ? `(${tag.count})` : ''}</span>
                                  {isSelected && <span className="text-blue-600">✓</span>}
                                </div>
                              );
                            })}
                          {tagSearch && !tags.find(t => (t.name || t).toLowerCase() === tagSearch.toLowerCase()) && (
                            <div
                              onClick={() => {
                                const currentTags = editingContact.tags || [];
                                setEditingContact({ ...editingContact, tags: [...currentTags, tagSearch] });
                                setTagSearch('');
                                setShowEditTagDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-600 border-t border-gray-200"
                            >
                              + Add "{tagSearch}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {editingContact.tags && editingContact.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {editingContact.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => {
                                setEditingContact({ ...editingContact, tags: editingContact.tags.filter((_, i) => i !== index) });
                              }}
                              className="text-blue-700 hover:text-blue-900"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    disabled={updateLoading}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateLoading}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed"
                  >
                    {updateLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                    <Save className="h-4 w-4" />
                    <span>Update Contact</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all">
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-t-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                    <Edit className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Bulk Edit Contacts</h2>
                    <p className="text-sm text-white/80">{selectedContacts.length} contacts selected</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowBulkEditModal(false);
                    setBulkEditLoading(false);
                  }}
                  disabled={bulkEditLoading}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleBulkEdit(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                    <select
                      value={bulkEditData.action}
                      onChange={(e) => setBulkEditData({ ...bulkEditData, action: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="set_group">Set Group</option>
                      <option value="remove_group">Remove Group</option>
                      <option value="add_tags">Add Tags</option>
                      <option value="remove_tags">Remove Tags</option>
                      <option value="replace_tags">Replace Tags</option>
                      <option value="set_subscription_status">Set Subscription Status</option>
                    </select>
                  </div>


                  {(bulkEditData.action === 'set_group') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                      <div className="relative" ref={bulkGroupDropdownRef}>
                        <input
                          type="text"
                          value={bulkEditData.group || ''}
                          onChange={(e) => {
                            setBulkEditData({ ...bulkEditData, group: e.target.value });
                            setGroupSearch(e.target.value);
                            setShowBulkGroupDropdown(true);
                          }}
                          onFocus={() => setShowBulkGroupDropdown(true)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Search or create group..."
                        />
                        {showBulkGroupDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {groups
                              .filter(g => (g.name || g).toLowerCase().includes(groupSearch.toLowerCase()))
                              .map((group) => (
                                <div
                                  key={group.name || group}
                                  onClick={() => {
                                    setBulkEditData({ ...bulkEditData, group: group.name || group });
                                    setGroupSearch('');
                                    setShowBulkGroupDropdown(false);
                          }}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                >
                                  {group.name || group} {group.count ? `(${group.count})` : ''}
                                </div>
                              ))}
                            {groupSearch && !groups.find(g => (g.name || g).toLowerCase() === groupSearch.toLowerCase()) && (
                              <div
                                onClick={() => {
                                  setBulkEditData({ ...bulkEditData, group: groupSearch });
                                  setGroupSearch('');
                                  setShowBulkGroupDropdown(false);
                                }}
                                className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-600 border-t border-gray-200"
                              >
                                + Create "{groupSearch}"
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(bulkEditData.action === 'add_tags' || bulkEditData.action === 'remove_tags' || bulkEditData.action === 'replace_tags') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                      <div className="relative" ref={bulkTagDropdownRef}>
                      <input
                        type="text"
                          value={tagSearch}
                          onChange={(e) => {
                            setTagSearch(e.target.value);
                            setShowBulkTagDropdown(true);
                          }}
                          onFocus={() => setShowBulkTagDropdown(true)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Search or add tags..."
                        />
                        {showBulkTagDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {tags
                              .filter(t => (t.name || t).toLowerCase().includes(tagSearch.toLowerCase()))
                              .map((tag) => {
                                const isSelected = bulkEditData.tags && bulkEditData.tags.includes(tag.name || tag);
                                return (
                                  <div
                                    key={tag.name || tag}
                                    onClick={() => {
                                      const currentTags = bulkEditData.tags || [];
                                      if (isSelected) {
                                        setBulkEditData({ ...bulkEditData, tags: currentTags.filter(t => t !== (tag.name || tag)) });
                                      } else {
                                        setBulkEditData({ ...bulkEditData, tags: [...currentTags, tag.name || tag] });
                                      }
                                      setTagSearch('');
                                    }}
                                    className={`px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm flex items-center justify-between ${isSelected ? 'bg-blue-50' : ''
                                      }`}
                                  >
                                    <span>{tag.name || tag} {tag.count ? `(${tag.count})` : ''}</span>
                                    {isSelected && <span className="text-blue-600">✓</span>}
                                  </div>
                                );
                              })}
                            {tagSearch && !tags.find(t => (t.name || t).toLowerCase() === tagSearch.toLowerCase()) && (
                              <div
                                onClick={() => {
                                  const currentTags = bulkEditData.tags || [];
                                  setBulkEditData({ ...bulkEditData, tags: [...currentTags, tagSearch] });
                                  setTagSearch('');
                                  setShowBulkTagDropdown(false);
                                }}
                                className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-600 border-t border-gray-200"
                              >
                                + Add "{tagSearch}"
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {bulkEditData.tags && bulkEditData.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {bulkEditData.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => {
                                  setBulkEditData({ ...bulkEditData, tags: bulkEditData.tags.filter((_, i) => i !== index) });
                                }}
                                className="text-purple-700 hover:text-purple-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {bulkEditData.action === 'set_subscription_status' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                      <select
                        value={bulkEditData.subscription_status}
                        onChange={(e) => setBulkEditData({ ...bulkEditData, subscription_status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="subscribed">Subscribed</option>
                        <option value="unsubscribed">Unsubscribed</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowBulkEditModal(false)}
                    disabled={bulkEditLoading}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={bulkEditLoading}
                    className="px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 disabled:from-gray-400 disabled:to-gray-400 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed"
                  >
                    {bulkEditLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Applying...</span>
                      </>
                    ) : (
                      <>
                    <Save className="h-4 w-4" />
                    <span>Apply Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-t-2xl p-6 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                    <Upload className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Import Contacts from CSV</h2>
                </div>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setUpdateOnDuplicate(false);
                    setImportCountryCode(DEFAULT_COUNTRY_CODE);
                    setImportLoading(false);
                    setImportProgress(0);
                  }}
                  disabled={importLoading}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">

              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>CSV Format:</strong> name, phone, email, group_name, tags
                </p>
                <p className="text-sm text-blue-700 mb-2">
                  • At least one of: name, phone, or email is required
                  • Phone: full international (+919876543210, 919876543210) or 10-digit local (9876543210) using the default country below
                  • Email format: user@example.com
                  • Tags: comma-separated values
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const sampleCSV = `name,phone,email,group_name,tags
Rahul Kumar,9876543210,rahul@example.com,Customers,"VIP, Premium"
Priya Singh,919876543210,priya@example.com,Leads,"New, Hot"
Bob Johnson,,bob@example.com,Suppliers,"Vendor, Trusted"
Alice Brown,+919811223344,,Partners,"Strategic, Long-term"
Charlie Wilson,9988776655,charlie@example.com,Customers,"Regular, Standard"`;

                    const blob = new Blob([sampleCSV], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'sample_contacts.csv';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-none cursor-pointer"
                >
                  📥 Download Sample CSV
                </button>
              </div>

              <form onSubmit={handleImportCSV}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default country for phone numbers</label>
                  <select
                    value={importCountryCode}
                    onChange={(e) => setImportCountryCode(e.target.value)}
                    disabled={importLoading}
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                  >
                    {COUNTRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-600">
                    Rows with exactly 10 digits (no country code) are stored with this prefix—default is India (+91).
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={updateOnDuplicate}
                      onChange={(e) => setUpdateOnDuplicate(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Update existing contacts</span>
                      <p className="text-xs text-gray-600 mt-1">
                        If checked, matching contacts (by phone or email) will be updated instead of skipped.
                        If unchecked, duplicates will be skipped.
                      </p>
                    </div>
                  </label>
                </div>

                {importPreview.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-900 mb-2">Preview (first 5 rows):</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            {importPreview[0]?.map((header, index) => (
                              <th key={index} className="px-2 py-1 text-left border">{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.slice(1).map((row, index) => (
                            <tr key={index}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="px-2 py-1 border">{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Progress Bar */}
                {importLoading && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Importing contacts...</span>
                      <span className="text-sm text-gray-600">{Math.round(importProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-gradient-to-r from-green-500 to-emerald-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${importProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportModal(false);
                      setUpdateOnDuplicate(false);
                      setImportCountryCode(DEFAULT_COUNTRY_CODE);
                      setImportLoading(false);
                      setImportProgress(0);
                    }}
                    disabled={importLoading}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!importFile || importLoading}
                    className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:from-gray-400 disabled:to-gray-400 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed"
                  >
                    {importLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Importing...</span>
                      </>
                    ) : (
                      <>
                    <Upload className="h-4 w-4" />
                    <span>Import Contacts</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;